import { useEffect, useRef, useMemo, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "@/state/hooks";
import { Group as Thread } from "@/data/schemas";
import { UserProfilePicture } from "@/ui/components/UserProfilePicture";
import NoteToSelfListItem from "@/ui/components/NoteToSelfListItem";
import { ListContainer, ListItem } from "@/ui/components/shared/ListItem";
import { safeMessage } from "@/utils/module_api";
import { Badge } from "@/ui/components/ui/badge";
import ThreadView from "@/features/chat/screens/ThreadView";
import CreateGroupPane from "@/features/threads/screens/CreateGroupPane";
import { threadService, db } from "@/services/module_api";
import { useStore } from "@/state/legacy_module_api";
import {
  crypto as switchCrypto,
  threadDisplayName,
  WebSocketEventType,
} from "@/core_bridge/module_api";
import { parseJwt } from "@/state/legacy_module_api";
import { parseThreadId } from "@/core_bridge/module_api";
import {
  createDirectThreadKey as makeDirectThreadKey,
  createGroupThreadKey,
  defaultConversationProjection,
} from "@/core_bridge/thread_keys";
import { GroupEventSync } from "@/features/groups/GroupEventSync";

type ThreadSelectPayload = {
  threadKey?: string;
  senderId?: string;
  receiverId?: string;
  groupId?: string;
  title?: string;
};

// Empty object constants to avoid creating new references
const EMPTY_OBJ: Record<string, any> = {};

// Helper to get the other user ID from a DM thread by parsing thread key
// DM thread keys are formatted as "dm:{userId1}_{userId2}" (sorted)
const getOtherUserId = (thread: Thread, currentUserId?: string): string | undefined => {
  if (!currentUserId) return undefined;
  const parsed = parseThreadId(thread.id);
  if (!parsed) return undefined;
  // Return the user ID that isn't the current user
  if (parsed.user1 === currentUserId) return parsed.user2;
  if (parsed.user2 === currentUserId) return parsed.user1;
  return undefined;
};

// Helper to check if thread is a DM (thread key starts with "dm:" and no communityId)
const isDMThread = (thread: Thread): boolean => {
  return thread.id.startsWith("dm:") && !thread.communityId;
};

const isSelfThread = (thread: Thread, currentUserId?: string): boolean => {
  if (!currentUserId) return false;

  if (thread.id.startsWith("self:")) {
    return thread.id.slice("self:".length) === currentUserId;
  }

  const withUserId =
    typeof (thread as any).withUserID === "string"
      ? (thread as any).withUserID
      : typeof (thread as any).withUserId === "string"
        ? (thread as any).withUserId
        : undefined;

  return isDMThread(thread) && withUserId === currentUserId;
};

const createDirectThreadKey = (senderId?: string, receiverId?: string): string | undefined => {
  if (!senderId || !receiverId) return undefined;
  return makeDirectThreadKey(senderId, receiverId);
};

export const ThreadList = ({
  onSelect,
}: {
  onSelect: (payload: ThreadSelectPayload) => void;
}) => {
  // Keep selectors stable to avoid unnecessary re-renders
  const threadMap = useStore((s: any) => s.threadMap ?? EMPTY_OBJ);
  const contactsById = useStore((s: any) => s.contactsById ?? EMPTY_OBJ);
  const environment = useStore((s: any) => s.environment);
  const communityMap = useStore((s: any) => s.communityMap ?? EMPTY_OBJ);
  // Get apiToken for parsing (primitive value - safe for selector)
  const apiToken = useStore((s: any) => s.apiToken);

  // Parse token data with useMemo to avoid creating new objects on every render
  const myUserId = useMemo(() => {
    if (!apiToken) {
      return undefined;
    }
    const parsed = parseJwt(apiToken);
    return parsed.user_id;
  }, [apiToken]);
  const profileRenderRef = useStore((s: any) => s.profileRenderRef || 0);

  // Derive threadsList with useMemo to avoid creating new arrays in selector
  const threadsList = useMemo(() => {
    const threads = Object.values(threadMap);
    return threads.map((thread: any) => {
      if (environment === "hi") {
        return {
          ...thread,
          lastMessage: thread.hiloMeta?.lastMessage ?? thread.lastMessage,
          updatedAt: thread.hiloMeta?.lastMessageAt ?? thread.updatedAt,
        };
      }
      return thread;
    });
  }, [threadMap, environment]);
  const ref = useRef(0);

  useEffect(() => {
    ref.current += 1;
  }, [environment]);

  const getThreadTitle = (thread: Thread) => {
    const otherUserId = getOtherUserId(thread, myUserId);
    const contact = otherUserId ? contactsById[otherUserId] : undefined;
    const isCommunity = !!thread?.communityId;
    const communityName = isCommunity ? communityMap[thread.communityId]?.name : undefined;
    return threadDisplayName(
      isDMThread(thread),
      isCommunity,
      thread?.name ?? "",
      communityName,
      contact?.fullHandle,
      contact?.username,
      contact?.deviceId
    );
  };

  const removeThread = useStore((s: any) => s.removeThread);

  const normalizedThreads = useMemo(() => {
    return threadsList
      .map((thread: Thread) => {
        const otherUserId = getOtherUserId(thread, myUserId);
        const isDm = isDMThread(thread);
        const withUserId =
          typeof (thread as any).withUserID === "string"
            ? (thread as any).withUserID
            : typeof (thread as any).withUserId === "string"
              ? (thread as any).withUserId
              : undefined;
        const kind =
          thread.communityId
            ? "community_channel"
            : isSelfThread(thread, myUserId)
              ? "self"
              : isDm
                ? "dm"
                : "group";
        const projection = defaultConversationProjection({
          kind,
          conversationId: thread.id,
          communityId: thread.communityId,
          currentUserId: myUserId,
          withUserId,
        });
        const isPlaceholder = !isDm && !thread.description && (!thread.members || thread.members.length === 0);
        return {
          thread,
          otherUserId,
          visibleInDefaultList: projection.visible,
          isPlaceholder,
          title: getThreadTitle(thread),
          updatedAt: typeof thread.updatedAt === "number" ? thread.updatedAt : 0,
          lastMessage: safeMessage(thread?.lastMessage),
          unread: typeof thread.unread === "number" ? thread.unread > 0 : !!thread.unread,
        };
      })
      .filter((entry) => entry.visibleInDefaultList)
      .sort((a, b) => {
        const timeDiff = b.updatedAt - a.updatedAt;
        if (timeDiff !== 0) return timeDiff;
        return a.thread.id.localeCompare(b.thread.id);
      });
  }, [threadsList, myUserId, contactsById, communityMap]);

  const handleDelete = async (thread: Thread) => {
    try {
      await db.deleteMessagesForThread(thread.id);
      await db.deleteThread(thread.id);
      removeThread(thread.id);
    } catch (error) {
      console.error("[ThreadList] Failed to delete thread", error);
    }
  };

  return (
    <ListContainer data-name="thread-list" key={ref.current + profileRenderRef}>
      <NoteToSelfListItem onSelect={(payload) => onSelect(payload)} />
      {normalizedThreads.length === 0 ? (
        <div className="px-4 py-8 text-center text-muted-foreground">
          <p className="font-medium">No threads found</p>
          <p className="text-sm">Start a thread first</p>
        </div>
      ) : (
        normalizedThreads.map(({ thread, otherUserId, isPlaceholder, title, lastMessage, unread }) => {
          const isGroupThread = !isDMThread(thread);
          return (
            <div
              key={thread.id}
              data-name="thread-item"
              data-thread-id={thread.id}
              onClick={() =>
                onSelect({
                  threadKey: thread.id,
                  senderId: myUserId,
                  receiverId: isGroupThread ? undefined : otherUserId,
                  groupId: isGroupThread
                    ? (thread.id.startsWith("group:") ? thread.id.slice("group:".length) : thread.id)
                    : undefined,
                  title,
                })
              }
              className="cursor-pointer"
            >
              <ListItem
                className="group"
                icon={
                  <UserProfilePicture
                    user={otherUserId ? contactsById[otherUserId] : undefined}
                    src={thread.image || undefined}
                    isGroup={isGroupThread}
                    isCommunity={!!thread.communityId}
                  />
                }
                title={title}
                subtitle={isPlaceholder ? (
                  <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Loading...
                  </span>
                ) : (lastMessage || undefined)}
                after={
                  <div className="flex items-center gap-2">
                    {unread ? <Badge variant="destructive">unread</Badge> : null}
                    <button
                      type="button"
                      className="opacity-70 hover:opacity-100"
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleDelete(thread);
                      }}
                      aria-label="Delete thread"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                }
                showChevron
              />
            </div>
          );
        })
      )}
    </ListContainer>
  );
};

// Empty selection constant
const EMPTY_SELECTION: Record<string, any> = {};

type RightPane = "thread" | "createGroup";

const ThreadsNav = () => {
  const appendMessage = useStore((s: any) => s.appendMessage);
  const setMessages = useStore((s: any) => s.setMessages);
  const selectThread = useStore((s: any) => s.selectThread);
  const setHasMore = useStore((s: any) => s.setHasMore);
  const setWsConnected = useStore((s: any) => s.setWsConnected);
  const selectedThreadId = useStore((s: any) => s.selectedThreadId);
  const updateMessage = useStore((s: any) => s.updateMessage);
  const setGroup = useStore((s: any) => s.setGroup);
  // Use stable empty object reference to avoid infinite re-renders
  const selection = useStore((s: any) => s.selection ?? EMPTY_SELECTION);
  const messagesByThread = useStore((s: any) => s.messagesByThread ?? EMPTY_OBJ);

  // Right pane state
  const [rightPane, setRightPane] = useState<RightPane>("thread");
  const threadsTabShowCreateGroup = useStore((s) => s.threadsTabShowCreateGroup);
  const setThreadsTabShowCreateGroup = useStore((s) => s.setThreadsTabShowCreateGroup);

  // Sync with store state (for button trigger from RootScreen)
  useEffect(() => {
    if (threadsTabShowCreateGroup) {
      setRightPane("createGroup");
      setThreadsTabShowCreateGroup(false);
    }
  }, [threadsTabShowCreateGroup, setThreadsTabShowCreateGroup]);

  useEffect(() => {
    threadService.setHandlers({
      onEvent: (evt: any) => {
        console.log("[threadService event]", evt);
        switch (evt.type) {
          case "thread_selected": {
            // Preserve existing title from store (handleSelect sets it before threadService fires back)
            const currentTitle = (useStore.getState() as any).selection?.title;
            selectThread?.({
              threadKey: evt.threadKey,
              withUserId: evt.withUserId || evt.receiverId,
              groupId: evt.groupId,
              senderId: evt.senderId,
              receiverId: evt.receiverId || evt.withUserId,
              title: evt.title || currentTitle,
            });
            break;
          }
          case "thread_snapshot":
            if (evt.threadKey) {
              setMessages?.(evt.threadKey, evt.messages || []);
              setHasMore?.(evt.threadKey, !!evt.hasMore);
            }
            break;
          case "thread_message_appended":
            if (evt.threadKey && evt.message) {
              const msg = evt.message;
              appendMessage?.(evt.threadKey, msg);
            }
            break;
          case "thread_error":
            if (evt.message) {
              toast.error(evt.message);
            }
            break;
          case "ws_state_changed":
            if (typeof evt.connected === "boolean") setWsConnected?.(evt.connected);
            break;
          case WebSocketEventType.MESSAGE_SENT:
            // Server confirmed message was sent - update status to DELIVERED
            if (evt.payload?.id) {
              console.log("[ThreadsNav] handling message_sent:", evt.payload);
              const msgId = evt.payload.id;
              // Persist to IndexedDB storage
              db.ackMessage(msgId).catch((err: any) => {
                console.warn("[ThreadsNav] db.ackMessage failed:", err);
              });
              // Update in-memory state
              const store = useStore.getState();
              const threads = store.messagesByThread || {};
              for (const [tid, msgs] of Object.entries(threads)) {
                if (Array.isArray(msgs) && msgs.some((m: any) => m.id === msgId)) {
                  updateMessage?.(tid, msgId, { status: "DELIVERED" });
                  break;
                }
              }
            }
            break;
          case WebSocketEventType.THREAD_CREATED:
          case WebSocketEventType.THREAD_UPDATED:
          case WebSocketEventType.THREAD_MEMBER_ADDED:
          case WebSocketEventType.THREAD_MEMBER_REMOVED:
          case WebSocketEventType.GROUP_METADATA_VERSION_CHANGED:
          case WebSocketEventType.GROUP_KEY_EPOCH_CHANGED:
          case WebSocketEventType.GROUP_TOMBSTONE:
          case WebSocketEventType.GROUP_MEMBER_ADDED:
          case WebSocketEventType.GROUP_MEMBER_REMOVED:
          case WebSocketEventType.GROUP_MEMBERSHIP_VERSION_CHANGED:
          case WebSocketEventType.RESYNC_REQUIRED:
          case WebSocketEventType.GROUP_MEMBERSHIP_RESYNC_REQUIRED: {
            if (evt.payload) {
              void GroupEventSync.handleEvent(evt.type, evt.payload as Record<string, any>);
            }
            break;
          }
          case WebSocketEventType.USER_ADDED: {
            const payload = evt.payload as Record<string, any> | undefined;
            const payloadGroupId = payload?.groupId ?? payload?.groupID;
            const payloadThreadKey = payload?.threadKey ?? payload?.thread_key;
            const hasGroupContext =
              (typeof payloadGroupId === "string" && payloadGroupId.length > 0) ||
              (typeof payloadThreadKey === "string" && payloadThreadKey.startsWith("group:"));

            if (payload && hasGroupContext) {
              void GroupEventSync.handleEvent(evt.type, payload);
            } else {
              console.log("[ThreadsNav] USER_ADDED treated as direct contact event", evt);
            }
            break;
          }
          default:
            break;
        }
      },
    });
    threadService.init().catch((err) =>
      console.warn("[ThreadsNav] threadService init failed", err),
    );
  }, []); // initialize once

  const handleSelect = (payload: ThreadSelectPayload) => {
    console.log("[ThreadsNav] handleSelect", payload);
    setRightPane("thread");
    const resolvedThreadId =
      payload.threadKey ||
      (payload.groupId
        ? createGroupThreadKey(payload.groupId)
        : createDirectThreadKey(payload.senderId, payload.receiverId));
    selectThread?.({
      threadKey: resolvedThreadId,
      receiverId: payload.receiverId,
      senderId: payload.senderId,
      groupId: payload.groupId,
      title: payload.title,
    });
    threadService.select({
      // For DMs, rely on canonical threadKey computed in service (dm:{user_a}_{user_b})
      threadKey: resolvedThreadId,
      withUserId: payload.receiverId,
      groupId: payload.groupId,
      senderId: payload.senderId,
      receiverId: payload.receiverId,
    });
  };

  const handleGroupCreated = () => {
    setRightPane("thread");
  };

  const hasSelection = useMemo(
    () =>
      !!(
        selection.receiverId ||
        selection.groupId ||
        selection.threadKey ||
        selectedThreadId
      ),
    [selection.receiverId, selection.groupId, selection.threadKey, selectedThreadId],
  );

  return (
    <div data-name="threads-nav" className="h-full grid grid-cols-[320px_1fr] min-h-0">
      <div data-name="threads-sidebar" className="border-r h-full overflow-auto">
        <ThreadList onSelect={handleSelect} />
      </div>
      <div data-name="thread-panel" className="h-full overflow-hidden min-h-0 flex flex-col">
        {rightPane === "createGroup" ? (
          <CreateGroupPane
            onBack={() => setRightPane("thread")}
            onGroupCreated={handleGroupCreated}
          />
        ) : hasSelection ? (
          <ThreadView
            receiverId={
              selection.receiverId ||
              selection.withUserId ||
              selectedThreadId
            }
            senderId={selection.senderId}
            groupId={selection.groupId}
            messages={selectedThreadId ? messagesByThread[selectedThreadId] || [] : []}
          />
        ) : (
          <div data-name="no-selection-placeholder" className="h-full flex items-center justify-center text-muted-foreground">
            Select a thread to start chatting.
          </div>
        )}
      </div>
    </div>
  );
};

export default ThreadsNav;
